"use client";

import { CartItem, CartItemWithGroup } from "@/types";
import React, { useState, useRef, useCallback, useEffect } from "react";
import { Button } from "../ui/button";
import { Trash2, Plus, Minus } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../ui/accordion";
import { Separator } from "../ui/separator";
import { Input } from "../ui/input";
import { formatPrice } from "@/lib/utils";
import Image from "next/image";
import Link from "next/link";
import {
  AlertDialog,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from "../ui/alert-dialog";

// Add a function to check if a part number has multiple decal variants
function hasMultipleDecalVariants(
  item: CartItemWithGroup,
  itemGroup: CartItemWithGroup[]
): boolean {
  if (itemGroup.length <= 1) return false;

  // Check if there are multiple items with the same part number but different decal numbers
  const uniqueDecals = new Set(itemGroup.map((i) => i.decal_number || ""));
  return uniqueDecals.size > 1;
}

function hasAttachments(item: CartItemWithGroup): boolean {
  return !!item.attachments && item.attachments.length > 0;
}

export function generateProductUrl(item: CartItem): string {
  // Use a standard format for all product URLs
  let url = `/${item.part_number.split("-")[0].toLowerCase()}/products/${item.part_number}`;

  // Add query parameters as needed
  const params = new URLSearchParams();

  // Add decal parameter for custom products
  if (item.decal_number) {
    params.set("decal", item.decal_number);
  }

  // Append parameters if any exist
  const queryString = params.toString();
  if (queryString) {
    url += `?${queryString}`;
  }

  return url;
}

interface SingleCartItemProps {
  item: CartItemWithGroup;
  quantity: number;
  isSheet: boolean;
  bulkCartOperation: (
    quoteNumber: string,
    operations: { item: CartItem; quantity: number }[]
  ) => Promise<{
    success: boolean;
    itemsAdded: number;
    itemsRemoved: number;
  }>;
}

export default function SingleCartItem({
  item,
  quantity,
  isSheet,
  bulkCartOperation,
}: SingleCartItemProps) {
  const [displayQuantity, setDisplayQuantity] = useState(quantity);
  const [isUpdating, setIsUpdating] = useState(false);
  const productUrl = generateProductUrl(item);
  const minQuantity = item.moq || 1;

  // Refs for debouncing
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pendingChangeRef = useRef<number>(0);
  const lastKnownServerQuantityRef = useRef(quantity);

  // Get active quote number from the first item in the group if available
  const activeQuoteNumber = item._group?.[0]?._quoteNumber || "";

  // Update refs when quantity prop changes (from server updates)
  useEffect(() => {
    lastKnownServerQuantityRef.current = quantity;
    setDisplayQuantity(quantity);
    // Clear any pending changes since server state has updated
    pendingChangeRef.current = 0;
  }, [quantity]);

  // Debounced function to sync with server
  const debouncedSyncWithServer = useCallback(async () => {
    if (!activeQuoteNumber || pendingChangeRef.current === 0) return;

    const changeAmount = pendingChangeRef.current;
    pendingChangeRef.current = 0;
    setIsUpdating(true);

    try {

      // Clean the item before operation to prevent circular references
      const cleanedItem = { ...item };
      delete (cleanedItem as CartItemWithGroup)._group;
      delete (cleanedItem as CartItemWithGroup)._quoteNumber;

      await bulkCartOperation(activeQuoteNumber, [
        { item: cleanedItem, quantity: changeAmount },
      ]);

      // Update our reference to the new expected server quantity
      lastKnownServerQuantityRef.current = displayQuantity;
    } catch (error) {
      console.error("Error syncing quantity with server:", error);
      // Revert to last known server state on error
      setDisplayQuantity(lastKnownServerQuantityRef.current);
      pendingChangeRef.current = 0;
    } finally {
      setIsUpdating(false);
    }
  }, [activeQuoteNumber, displayQuantity, item, bulkCartOperation]);

  // Function to schedule a server sync with debouncing
  const scheduleServerSync = useCallback(() => {
    // Clear existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Schedule new sync after 500ms of inactivity
    timeoutRef.current = setTimeout(() => {
      debouncedSyncWithServer();
    }, 500);
  }, [debouncedSyncWithServer]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Handler for decreasing quantity
  const handleDecrease = useCallback(() => {
    if (displayQuantity <= minQuantity || !activeQuoteNumber || isUpdating)
      return;

    // Update UI immediately (optimistic update)
    setDisplayQuantity((prev) => prev - 1);

    // Track the pending change
    pendingChangeRef.current -= 1;

    // Schedule server sync
    scheduleServerSync();
  }, [
    displayQuantity,
    minQuantity,
    activeQuoteNumber,
    isUpdating,
    scheduleServerSync,
  ]);

  // Handler for increasing quantity
  const handleIncrease = useCallback(() => {
    if (!activeQuoteNumber || isUpdating) return;

    // Update UI immediately (optimistic update)
    setDisplayQuantity((prev) => prev + 1);

    // Track the pending change
    pendingChangeRef.current += 1;

    // Schedule server sync
    scheduleServerSync();
  }, [activeQuoteNumber, isUpdating, scheduleServerSync]);

  // Handler for direct quantity input
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value, 10);
    if (isNaN(value) || value < minQuantity) return;

    setDisplayQuantity(value);
  };

  // Handler for when the quantity input loses focus
  const handleInputBlur = useCallback(async () => {
    if (!activeQuoteNumber || isUpdating) return;

    // Calculate the total change needed
    const currentServerQuantity = lastKnownServerQuantityRef.current;
    const newQuantity = displayQuantity;

    if (newQuantity === currentServerQuantity) {
      // No change needed, clear any pending changes
      pendingChangeRef.current = 0;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      return;
    }

    // Clear any pending incremental changes and set the exact change needed
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    const totalChange = newQuantity - currentServerQuantity;
    pendingChangeRef.current = totalChange;

    // Immediately sync for input blur (don't wait for debounce)
    setIsUpdating(true);

    try {

      const cleanedItem = { ...item };
      delete (cleanedItem as CartItemWithGroup)._group;
      delete (cleanedItem as CartItemWithGroup)._quoteNumber;

      await bulkCartOperation(activeQuoteNumber, [
        { item: cleanedItem, quantity: totalChange },
      ]);

      // Update our reference to the new server quantity
      lastKnownServerQuantityRef.current = newQuantity;
      pendingChangeRef.current = 0;
    } catch (error) {
      console.error("Error updating quantity on blur:", error);
      // Reset display quantity to match server state on error
      setDisplayQuantity(currentServerQuantity);
      pendingChangeRef.current = 0;
    } finally {
      setIsUpdating(false);
    }
  }, [activeQuoteNumber, displayQuantity, item, bulkCartOperation, isUpdating]);

  // Handler for removing an item completely
  const removeItem = async (partNumber: string, decalNumber?: string) => {
    if (!activeQuoteNumber) return;

    try {

      // Create a filter item that matches what we want to remove
      const filterItem: CartItem = {
        ...item,
        part_number: partNumber,
        decal_number: decalNumber,
      };

      // Clean circular references
      delete (filterItem as CartItemWithGroup)._group;
      delete (filterItem as CartItemWithGroup)._quoteNumber;

      await bulkCartOperation(activeQuoteNumber, [
        { item: filterItem, quantity: -quantity },
      ]);
    } catch (error) {
      console.error("Error removing item:", error);
    }
  };

  // Helper function to add a single item of a specific variant
  const addItem = async (partNumber: string, decalNumber?: string) => {
    if (!activeQuoteNumber) return;

    try {

      // Create a filter item that matches what we want to add
      const filterItem: CartItem = {
        ...item,
        part_number: partNumber,
        decal_number: decalNumber,
      };

      // Clean circular references
      delete (filterItem as CartItemWithGroup)._group;
      delete (filterItem as CartItemWithGroup)._quoteNumber;

      await bulkCartOperation(activeQuoteNumber, [
        { item: filterItem, quantity: 1 },
      ]);
    } catch (error) {
      console.error("Error adding item:", error);
    }
  };

  // Helper function to remove a single item of a specific variant
  const removeSingleItem = async (partNumber: string, decalNumber?: string) => {
    if (!activeQuoteNumber) return;

    try {

      // Create a filter item that matches what we want to remove
      const filterItem: CartItem = {
        ...item,
        part_number: partNumber,
        decal_number: decalNumber,
      };

      // Clean circular references
      delete (filterItem as CartItemWithGroup)._group;
      delete (filterItem as CartItemWithGroup)._quoteNumber;

      await bulkCartOperation(activeQuoteNumber, [
        { item: filterItem, quantity: -1 },
      ]);
    } catch (error) {
      console.error("Error removing single item:", error);
    }
  };

  // Finding the full group this item belongs to (from parent CartItems component)
  const group = item._group || [item];

  // Check if item has attachments or is part of a multiple decal variations group
  const hasItemAttachments = hasAttachments(item);
  const hasDecalVariants = hasMultipleDecalVariants(item, group);
  const shouldRenderAsAccordion = hasItemAttachments || hasDecalVariants;

  // If item has attachments or is part of a decal variation group, render as accordion
  if (shouldRenderAsAccordion) {
    return (
      <div className='relative'>
        {/* Trash button positioned absolutely top-right with AlertDialog */}
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button
              variant='ghost'
              size='icon'
              className='absolute right-0 top-0 z-10 h-8 w-8 text-destructive'
              title='Remove all variants'
            >
              <Trash2 className='h-4 w-4' />
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Remove item(s) from cart?</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to remove this item
                {hasDecalVariants ? " and all its decal variations" : ""} from
                your cart? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => removeItem(item.part_number)}
                className='bg-destructive text-white hover:bg-destructive/90'
              >
                Remove
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <div className='flex flex-col space-y-2'>
          <Accordion type='single' collapsible className='w-full'>
            <AccordionItem value='item' className='border-0'>
              {/* Product Info Section */}
              <div className='flex flex-col w-full'>
                <div className='flex items-start w-full'>
                  <AccordionTrigger className='py-4 px-0 hover:no-underline flex-1'>
                    <div className='flex items-start space-x-4 w-full text-left'>
                      {/* Product Image */}
                      <div
                        className={`relative ${isSheet ? "h-16 w-16" : "h-20 w-20"} overflow-hidden rounded-md bg-muted flex-shrink-0`}
                      >
                        {item.image ? (
                          <Image
                            src={item.image}
                            alt={item.title}
                            fill
                            className='object-cover'
                            sizes={isSheet ? "64px" : "80px"}
                          />
                        ) : (
                          <div className='flex h-full items-center justify-center bg-secondary'>
                            <span className='text-xs text-muted-foreground'>
                              No image
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Basic Product Info */}
                      <div className='flex-1'>
                        <Link
                          href={generateProductUrl(item)}
                          className='hover:underline'
                        >
                          <h3 className='font-medium text-sm md:text-base text-left'>
                            {item.title}
                          </h3>
                        </Link>
                        <p className='text-xs text-muted-foreground'>
                          Part Number: {item.part_number.toUpperCase()}
                        </p>
                        {/* For products with multiple decal variations, display that info in the header */}
                        {hasDecalVariants ? (
                          <p className='text-xs text-primary mt-1'>
                            {group.length} decal variations
                          </p>
                        ) : item.decal_number ? (
                          <p className='text-xs text-muted-foreground'>
                            Decal: {item.decal_number}
                          </p>
                        ) : null}
                        {hasItemAttachments && (
                          <p className='text-xs text-primary mt-1'>
                            Includes: {item.attachments?.length ?? 0} items
                          </p>
                        )}
                      </div>
                    </div>
                  </AccordionTrigger>
                </div>

                {/* Quantity and Price Section - Outside of accordion content */}
                {(!hasDecalVariants || hasItemAttachments) && (
                  <div className='mt-2 mb-4 flex items-center justify-between px-4'>
                    <div className='flex items-center space-x-2'>
                      <Button
                        variant='outline'
                        size='icon'
                        className='h-8 w-8'
                        onClick={handleDecrease}
                        disabled={
                          displayQuantity <= (item.moq || 1) || isUpdating
                        }
                      >
                        <Minus className='h-4 w-4' />
                      </Button>

                      <Input
                        type='number'
                        min={item.moq}
                        value={displayQuantity}
                        onChange={handleInputChange}
                        onBlur={handleInputBlur}
                        className='h-8 w-24 text-center'
                        disabled={isUpdating}
                      />

                      <Button
                        variant='outline'
                        size='icon'
                        className='h-8 w-8'
                        onClick={handleIncrease}
                        disabled={isUpdating}
                      >
                        <Plus className='h-4 w-4' />
                      </Button>

                      {/* Show updating indicator */}
                      {isUpdating && (
                        <div className='h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent' />
                      )}
                    </div>

                    <div className='text-right'>
                      <p className='font-medium'>
                        {formatPrice(item.price * displayQuantity)}{" "}
                        <span className='text-xs text-muted-foreground'>
                          excl. GST
                        </span>
                      </p>
                      {!isSheet && (
                        <p className='text-sm text-muted-foreground'>
                          {formatPrice(item.price)}{" "}
                          <span className='text-xs'>excl. GST</span> each
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Accordion Content - Details and Attachments/Decal Variations */}
              <AccordionContent>
                <div className='space-y-4 pt-2 pb-4'>
                  {/* Conditional rendering based on item type */}
                  {hasItemAttachments && (
                    /* Attachments List */
                    <div className='space-y-2'>
                      <h4 className='text-sm font-medium mb-2'>
                        Included Items
                      </h4>
                      {(item.attachments ?? []).map((attachment, index) => (
                        <div
                          key={attachment.item_id || index}
                          className='flex items-center border border-muted p-2 rounded-md relative'
                        >
                          {/* Attachment Image */}
                          <div className='h-10 w-10 relative flex-shrink-0 bg-muted rounded-md overflow-hidden'>
                            {attachment.image ? (
                              <Image
                                src={attachment.image}
                                alt={attachment.title}
                                fill
                                className='object-cover'
                                sizes='40px'
                              />
                            ) : (
                              <div className='h-full w-full flex items-center justify-center'>
                                <span className='text-[10px] text-muted-foreground'>
                                  No image
                                </span>
                              </div>
                            )}
                          </div>

                          {/* Attachment Details */}
                          <div className='flex-1 min-w-0 ml-3'>
                            <Link
                              href={generateProductUrl(attachment)}
                              className='hover:underline'
                            >
                              <p className='text-sm font-medium'>
                                {attachment.title}
                              </p>
                            </Link>
                            <p className='text-xs text-muted-foreground'>
                              {attachment.part_number}
                            </p>
                            {attachment.decal_number && (
                              <p className='text-xs text-muted-foreground'>
                                Decal: {attachment.decal_number}
                              </p>
                            )}
                          </div>
                          {/* Quantity block on the right */}
                          <div className='ml-2 bg-muted px-2 py-1 rounded text-xs font-bold text-foreground min-w-[32px] text-center'>
                            {attachment.quantity || 1}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {hasDecalVariants && !hasItemAttachments && (
                    /* Decal Variations List */
                    <div className='space-y-2'>
                      <h4 className='text-sm font-medium mb-2'>
                        Decal Variations
                      </h4>
                      {group.map((variation, index) => (
                        <div
                          key={`${variation.decal_number || ""}-${index}`}
                          className='flex items-center justify-between border border-muted p-2 rounded-md relative'
                        >
                          <div className='flex items-center'>
                            {/* Variation Image */}
                            <div className='h-10 w-10 relative flex-shrink-0 bg-muted rounded-md overflow-hidden'>
                              {variation.image ? (
                                <Image
                                  src={variation.image}
                                  alt={variation.title}
                                  fill
                                  className='object-cover'
                                  sizes='40px'
                                />
                              ) : (
                                <div className='h-full w-full flex items-center justify-center'>
                                  <span className='text-[10px] text-muted-foreground'>
                                    No image
                                  </span>
                                </div>
                              )}
                            </div>

                            {/* Simplified Variation Details - Just show Decal Number */}
                            <div className='min-w-0 ml-3'>
                              {variation.decal_number && (
                                <p className='text-sm font-medium'>
                                  Decal: {variation.decal_number}
                                </p>
                              )}
                            </div>
                          </div>

                          {/* Quantity controls for each variation */}
                          <div className='flex items-center space-x-2'>
                            <Button
                              variant='outline'
                              size='icon'
                              className='h-6 w-6'
                              onClick={() =>
                                removeSingleItem(
                                  variation.part_number,
                                  variation.decal_number
                                )
                              }
                              disabled={(variation.moq || 1) >= (quantity || 1)}
                            >
                              <Minus className='h-3 w-3' />
                            </Button>

                            <span className='text-sm font-medium w-6 text-center'>
                              {quantity || 1}
                            </span>

                            <Button
                              variant='outline'
                              size='icon'
                              className='h-6 w-6'
                              onClick={() =>
                                addItem(
                                  variation.part_number,
                                  variation.decal_number
                                )
                              }
                            >
                              <Plus className='h-3 w-3' />
                            </Button>

                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button
                                  variant='ghost'
                                  size='icon'
                                  className='h-6 w-6 text-destructive'
                                  title='Remove this decal variation'
                                >
                                  <Trash2 className='h-3 w-3' />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>
                                    Remove decal variation?
                                  </AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Are you sure you want to remove this decal
                                    variation from your cart? This action cannot
                                    be undone.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={() =>
                                      removeItem(
                                        variation.part_number,
                                        variation.decal_number
                                      )
                                    }
                                    className='bg-destructive text-white hover:bg-destructive/90'
                                  >
                                    Remove
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
        {isSheet && <Separator />}
      </div>
    );
  }

  // Regular item without attachments or decal variations - use existing layout
  return (
    <div>
      <div className='flex items-start space-x-4 py-4 w-full'>
        {/* Product Image - Now a clickable link */}
        <Link
          href={productUrl}
          className={`relative ${
            isSheet ? "h-16 w-16" : "h-20 w-20"
          } overflow-hidden rounded-md bg-muted flex-shrink-0`}
        >
          {item.image ? (
            <Image
              src={item.image}
              alt={item.title}
              fill
              className='object-cover'
              sizes={isSheet ? "64px" : "80px"}
            />
          ) : (
            <div className='flex h-full items-center justify-center bg-secondary'>
              <span className='text-xs text-muted-foreground'>No image</span>
            </div>
          )}
        </Link>

        {/* Product Details */}
        <div className='flex flex-1 flex-col'>
          <div className='flex items-start gap-2'>
            <div className='flex-1 pr-4 max-w-[calc(100%-40px)]'>
              <Link href={productUrl} className='hover:underline'>
                <h3 className='font-medium break-words'>{item.title}</h3>
              </Link>
              <p className='text-sm text-muted-foreground'>
                Part Number: {item.part_number.toUpperCase()}
              </p>
              {item.decal_number && (
                <p className='text-sm text-muted-foreground'>
                  Decal: {item.decal_number}
                </p>
              )}
            </div>
            <div className='shrink-0'>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    variant={isSheet ? "outline" : "ghost"}
                    size={isSheet ? "sm" : "icon"}
                    className={
                      isSheet
                        ? "text-sm text-destructive shrink-0"
                        : "h-8 w-8 text-destructive shrink-0"
                    }
                  >
                    <Trash2 className='h-4 w-4' />
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Remove item from cart?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Are you sure you want to remove this item from your cart?
                      This action cannot be undone.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={() => removeItem(item.part_number)}
                      className='bg-destructive text-white hover:bg-destructive/90'
                    >
                      Remove
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>

          {/* Quantity and Price */}
          <div className='mt-2 flex items-center justify-between'>
            <div className='flex items-center space-x-2'>
              <Button
                variant='outline'
                size='icon'
                className='h-8 w-8'
                onClick={handleDecrease}
                disabled={displayQuantity <= (item.moq || 1) || isUpdating}
              >
                <Minus className='h-4 w-4' />
              </Button>

              <Input
                type='number'
                min={item.moq}
                value={displayQuantity}
                onChange={handleInputChange}
                onBlur={handleInputBlur}
                className='h-8 w-24 text-center'
                disabled={isUpdating}
              />

              <Button
                variant='outline'
                size='icon'
                className='h-8 w-8'
                onClick={handleIncrease}
                disabled={isUpdating}
              >
                <Plus className='h-4 w-4' />
              </Button>

              {/* Show updating indicator */}
              {isUpdating && (
                <div className='h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent' />
              )}
            </div>
            <div className='text-right'>
              <p className='font-medium'>
                {formatPrice(item.price * displayQuantity)}{" "}
                <span className='text-xs text-muted-foreground'>excl. GST</span>
              </p>
              {!isSheet && (
                <p className='text-sm text-muted-foreground'>
                  {formatPrice(item.price)}{" "}
                  <span className='text-xs'>excl. GST</span> each
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
      {isSheet && <Separator />}
    </div>
  );
}
